import { IEventSystem } from "@dhx/ts-common/events";
import { SelectionEvents, ISelectionEventsHandlersMap, Id } from "@dhx/ts-common/types";
import { DataCollection, DataEvents, IDataEventsHandlersMap, IDataItem } from "@dhx/ts-data";
import { ISelectionConfig, ISelection } from "./types";

export class Selection implements ISelection {
	public config: ISelectionConfig;
	public events: IEventSystem<
		SelectionEvents | DataEvents,
		ISelectionEventsHandlersMap & IDataEventsHandlersMap
	>;
	private _selected: Id[];
	private _data: DataCollection;
	private _nextSelection: Id;

	constructor(config: ISelectionConfig, data: DataCollection, events: IEventSystem<any>) {
		this.config = config;
		this.events = events;
		this._data = data;
		this._selected = [];

		this._data.events.on(DataEvents.removeAll, () => {
			this._selected = [];
		});
		if (typeof this.config.multiselection === "string") {
			const types = ["click", "ctrlClick"];
			if (!types.includes(this.config.multiselection)) {
				this.config.multiselection = false;
			}
		}

		this._data.events.on(DataEvents.beforeRemove, obj => {
			this._nextSelection = null;
			if (this._selected.length === 1) {
				const next = this._data.getIndex(obj.id);
				const count = this._data.getLength();
				if (count > 1) {
					const lastIndex = count == next - 1 ? next - 1 : next + 1;
					this._nextSelection = this._data.getId(lastIndex);
				}
			}
		});
		this._data.events.on(DataEvents.afterRemove, obj => {
			const hasIndex = this._selected.indexOf(obj.id);
			if (hasIndex !== -1) {
				this._selected.splice(hasIndex, 1);
			}

			if (this._nextSelection) {
				this.add(this._nextSelection);
				this._nextSelection = null;
			}
		});
	}
	enable() {
		this.config.disabled = false;
	}
	disable() {
		this.remove();
		this.config.disabled = true;
	}
	getId(): Id | Id[] | undefined {
		if (this.config.multiselection) {
			return this._selected;
		}
		return this._selected[0];
	}
	getItem(): IDataItem | IDataItem[] {
		if (this.config.multiselection) {
			return this._selected.map(id => this._data.getItem(id));
		}

		return this._selected.length ? this._data.getItem(this._selected[0]) : null;
	}
	contains(id?: Id): boolean {
		if (id) {
			return this._selected.includes(id);
		}
		return this._selected.length > 0;
	}
	remove(id?: Id): void {
		if (id) {
			this._unselectItem(id);
		} else {
			this._selected.forEach(selectedId => this._unselectItem(selectedId));
			this._selected = [];
		}
	}
	add(id?: Id, isCtrl?: boolean, isShift?: boolean, silent?: boolean) {
		if (this.config.disabled) {
			return;
		}

		if (typeof id === "undefined") {
			const unSelected = this._data.serialize().filter(({ id }) => {
				return !this._selected.includes(id);
			});
			unSelected.forEach(({ id }) => {
				this._addMulti(id, silent);
			});
			return;
		}

		const multi = this.config.multiselection;
		if (isShift && this._selected.length && multi) {
			this._addMulti(id, silent);
		} else {
			this._addSingle(id, multi && (multi !== "ctrlClick" || isCtrl), silent);
		}
	}
	destructor() {
		this._selected.forEach(selectedId => this._unselectItem(selectedId, true));
	}
	private _addMulti(id: Id, silent?: boolean) {
		const last = this._selected[this._selected.length - 1];
		let i1 = this._data.getIndex(last);
		let i2 = this._data.getIndex(id);

		if (i1 > i2) {
			[i1, i2] = [i2, i1];
		}

		for (i1; i1 <= i2; i1++) {
			const id = this._data.getId(i1);
			this._selectItem(id, silent);
		}
	}
	private _addSingle(id: Id, isCtrl?: boolean, silent?: boolean) {
		if (!isCtrl) {
			this._selected.forEach(selectedId => {
				if (selectedId != id) this._unselectItem(selectedId);
			});
		}

		if (isCtrl && this._selected.includes(id)) {
			this._unselectItem(id, silent);
		} else {
			this._selectItem(id, silent);
		}
	}
	private _selectItem(id: Id, silent?: boolean) {
		const item = this._data.getItem(id);
		if (!item || this._data.getMeta(item, "selected")) return;

		if (!silent && !this.events.fire(SelectionEvents.beforeSelect, [id])) return;
		this._selected.push(id);
		this._data.setMeta(item, "selected", true);
		if (!silent) this.events.fire(SelectionEvents.afterSelect, [id]);
	}
	private _unselectItem(id: Id, silent?: boolean) {
		if (!silent && !this.events.fire(SelectionEvents.beforeUnSelect, [id])) return;
		this._selected = this._selected.filter(selectedId => selectedId !== id);
		this._data.setMeta(this._data.getItem(id), "selected", false);
		if (!silent) this.events.fire(SelectionEvents.afterUnSelect, [id]);
	}
}
